import { useCallback, useState } from 'react';

import useFetch from 'hooks/useFetch';
import useRetry from 'hooks/useRetry';

import useTranslation from './hooks/useTranslation';

import { CHEMICAL_DB_API_HOST, JSON_HEADERS, PRESCRIPTIONS_API } from './constants';
import type {
    CreatePrescription,
    CreatePrescriptionResponse,
    FetchPrescription,
    FetchedPrescription
} from './types';

const usePrescription = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { authenticatedGet, authenticatedPost } = useFetch({
        apiHost: CHEMICAL_DB_API_HOST,
        headers: JSON_HEADERS
    });
    const { withRetry } = useRetry();
    const { FETCH_FAILED, SAVE_FAILED } = useTranslation();

    const createPrescription: CreatePrescription = useCallback(
        async ({ prescription }) => {
            try {
                setIsLoading(true);

                return await withRetry(
                    () =>
                        authenticatedPost<CreatePrescriptionResponse>(
                            PRESCRIPTIONS_API,
                            JSON.stringify(prescription)
                        ),
                    SAVE_FAILED,
                    () => createPrescription({ prescription })
                );
            } finally {
                setIsLoading(false);
            }
        },

        [authenticatedPost, withRetry, SAVE_FAILED]
    );

    const fetchPrescription: FetchPrescription = useCallback(
        async ({ id }) => {
            try {
                setIsLoading(true);

                return await withRetry(
                    () => authenticatedGet<FetchedPrescription>(`${PRESCRIPTIONS_API}/${id}`),
                    FETCH_FAILED,
                    () => fetchPrescription({ id })
                );
            } finally {
                setIsLoading(false);
            }
        },

        [authenticatedGet, withRetry, FETCH_FAILED]
    );

    return { createPrescription, fetchPrescription, isLoading };
};

export default usePrescription;
